import { useAction, useAtom } from '@reatom/npm-react';
import { Product, SearchProductEntity } from '@trwl/backend.api';
import { Path } from '@trwl/shared/lib/router';
import {
  dislikeAction,
  isItemLiked,
  likeAction,
  likesAtom,
  setDuration,
  setShow,
} from '@trwl/store';
import { capitalizeText, formatNumber } from '@trwl/util';
import { preprocessImage } from '@trwl/util/preprocessImage';
import cn from 'classnames';
import { useUnit } from 'effector-react';
import Image from 'next/image';
import Link from 'next/link';
import {
  FC,
  HTMLAttributeAnchorTarget,
  HTMLAttributes,
  useCallback,
  useMemo,
} from 'react';
import { isMobile } from 'react-device-detect';
import { FaHeart } from 'react-icons/fa';

export interface ProductCardProps extends HTMLAttributes<HTMLDivElement> {
  product: Product | SearchProductEntity;
  loading?: 'eager' | 'lazy' | undefined;
  priority?: boolean;
  showLikes?: boolean;
  target?: HTMLAttributeAnchorTarget | undefined;
}

export const ProductCard: FC<ProductCardProps> = ({
  product,
  loading,
  priority,
  showLikes,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  target,
  ...rest
}) => {
  const [likes] = useAtom(likesAtom);
  const like = useAction(likeAction);
  const dislike = useAction(dislikeAction);
  const [changeShow, changeDuration] = useUnit([setShow, setDuration]);

  const onLinkClick = useCallback(() => {
    changeDuration(20000);
    changeShow(true);
  }, [changeShow, changeDuration]);

  const logo = useMemo(() => {
    if ('logo' in product) {
      return product.logo;
    }
    if ('logoUrl' in product) {
      return product.logoUrl;
    }
    return '';
  }, [product]);

  const stock = useMemo(() => {
    if (!product.minPrice) return false;
    if ('stock' in product) {
      return product.stock;
    }
    if ('isStock' in product) {
      return product.isStock;
    }
    return false;
  }, [product]);

  const formattedPrice = useMemo(() => {
    if (!stock) return 'Нет в наличии';
    return 'от ' + formatNumber(Number(product.minPrice)).concat('₽');
  }, [product, stock]);

  const handleLike = () => {
    if (isItemLiked(likes, product)) {
      dislike(product);
    } else {
      like(product);
    }
  };

  const liked = useMemo(
    () => Boolean(isItemLiked(likes, product)),
    [likes, product],
  );

  const productUrl =
    product.id > 0
      ? `${Path.PRODUCT}/${product.id}`
      : `${Path.PRODUCT}?article=${product.article}`;

  return (
    <article {...rest} aria-label={`Товар: ${product.title}`}>
      <Link
        onClick={onLinkClick}
        href={productUrl}
        tabIndex={0}
        aria-label={`Подробнее о товаре: ${product.title}`}
        prefetch={false}
        className={cn(
          'flex flex-col gap-1 relative',
          !stock && 'opacity-70',
          'focus:outline-none',
        )}
      >
        <Image
          unoptimized
          width={550}
          height={550}
          className={cn(
            'self-center aspect-square select-none',
            !isMobile && 'hover:scale-[1.03] duration-300',
          )}
          src={preprocessImage(logo, 550)}
          alt={product.title}
          loading={loading}
          priority={priority}
        />
        <h3 className="text-[14px] lg:text-[15px] line-clamp-1 font-normal">
          {capitalizeText(product.title)}
        </h3>
        <p className="font-normal text-[14px] md:text-[15px] lg:text-[16px] top-[5px]">
          <span>{formattedPrice}</span>
        </p>
        {showLikes && (
          <button
            type="button"
            aria-label={liked ? 'Убрать из избранного' : 'Добавить в избранное'}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleLike();
            }}
            className="transition cursor-pointer absolute z-30 w-[24px] h-[24px] p-[3px] left-1 top-2 hover:scale-125 duration-300 bg-transparent border-0"
            tabIndex={0}
          >
            <FaHeart
              className={'w-full h-full'}
              style={{
                color: liked ? '#DE392E' : '#6F6F6F',
              }}
              aria-hidden="true"
            />
          </button>
        )}
      </Link>
    </article>
  );
};
